import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

/** East Africa + common booker origins — shown first in the picker. */
const PRIORITY_ISOS = ['KE', 'TZ', 'UG', 'RW', 'BI', 'GB', 'US', 'DE', 'FR', 'NL'];

/** Tie-break shared dial codes (e.g. +1 covers US/CA and many territories). */
const DIAL_CODE_DEFAULT_ISO = {
  1: 'US',
  7: 'RU',
  44: 'GB',
  47: 'NO',
  61: 'AU',
  64: 'NZ',
};

let cachedList = null;
let cachedDisplayNames = null;

function getDisplayNames() {
  if (cachedDisplayNames !== null) return cachedDisplayNames;
  try {
    cachedDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    cachedDisplayNames = null;
  }
  return cachedDisplayNames;
}

function countryLabel(iso) {
  const names = getDisplayNames();
  if (!names) return iso;
  try {
    return names.of(iso) || iso;
  } catch {
    return iso;
  }
}

function stripDiacritics(text) {
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * @returns {{ iso: string, code: string, name: string, priority: number }[]}
 */
export function getPhoneCountries() {
  if (cachedList) return cachedList;

  const priorityIndex = new Map(PRIORITY_ISOS.map((iso, i) => [iso, i]));

  cachedList = getCountries()
    .map((iso) => {
      let callingCode;
      try {
        callingCode = getCountryCallingCode(iso);
      } catch {
        return null;
      }
      return {
        iso,
        code: `+${callingCode}`,
        name: countryLabel(iso),
        priority: priorityIndex.has(iso) ? priorityIndex.get(iso) : 1000,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    });

  return cachedList;
}

/**
 * Resolve an ISO country from a dial code string (e.g. "+254" or "254").
 * @param {string} dialCode
 * @param {string} [preferredIso]
 */
export function isoFromDialCode(dialCode, preferredIso) {
  const digits = String(dialCode || '').replace(/\D/g, '');
  if (!digits) return null;

  const countries = getPhoneCountries().filter((c) => c.code === `+${digits}`);
  if (!countries.length) return null;
  if (preferredIso && countries.some((c) => c.iso === preferredIso)) return preferredIso;

  const defaultIso = DIAL_CODE_DEFAULT_ISO[digits];
  if (defaultIso && countries.some((c) => c.iso === defaultIso)) return defaultIso;

  return countries[0].iso;
}

/**
 * @param {string} query
 * @param {number} [limit]
 */
export function searchPhoneCountries(query, limit = 60) {
  const countries = getPhoneCountries();
  const q = stripDiacritics(String(query || '').trim().toLowerCase());
  if (!q) return countries.slice(0, limit);

  const scored = countries
    .map((country) => {
      const name = stripDiacritics(country.name.toLowerCase());
      const iso = country.iso.toLowerCase();
      const code = country.code.replace('+', '');

      let score = 0;
      if (iso === q) score = 100;
      else if (name === q) score = 90;
      else if (country.code === q || country.code === `+${q}`) score = 85;
      else if (iso.startsWith(q)) score = 70;
      else if (name.startsWith(q)) score = 60;
      else if (code.startsWith(q)) score = 55;
      else if (name.includes(q)) score = 40;
      else if (code.includes(q)) score = 30;

      return score > 0 ? { country, score } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.country.priority !== b.country.priority) return a.country.priority - b.country.priority;
      return a.country.name.localeCompare(b.country.name);
    });

  return scored.slice(0, limit).map((s) => s.country);
}

export function getCountryByIso(iso) {
  return getPhoneCountries().find((c) => c.iso === iso) || null;
}
